import Playback from "../playback/playback"
import Player from "../player"
import Theme, { ThemeOptions } from "../theme/theme"

/**
 * All events from the native environment that can be handled by the library
 * 
 * @author Stan Hurks
 */
export type ReceivedEvent =
    'initialize'
    | 'theme'
    | 'playback'
    | 'muted'
    | 'unmuted'

/**
 * All events that can be send to the native environment.
 */
export type SentEvent = 'log'
    | 'pause'
    | 'play'
    | 'mute'
    | 'unmute'

/**
 * The main entry point for all native functionality. (ios/android)
 * 
 * @author Stan Hurks
 */
export default class Native {
    /**
     * The custom message handler of bitmovin, responsible for the
     * communication between android/ios and this library.
     */
    public static customMessageHandler: any|undefined = undefined

    /**
     * The theme
     */
    public static theme?: Theme = undefined

    /**
     * The playback
     */
    public static playback?: Playback = undefined

    /**
     * The entry point for the native functionality
     */
    public static main = () => {
        Native.customMessageHandler = (window as any).bitmovin.customMessageHandler
        if (Native.customMessageHandler) {
            Native.log('Native environment detected (iOS/android)')
            Native.registerEvents()
        }
    }

    /**
     * Register all events sent from the native environment
     */
    private static registerEvents = () => {
        Native.on('initialize', (data?: string) => {
            Native.log('Initializing player functionality...')
        })
        Native.on('theme', (options?: ThemeOptions) => {
            if (!options) {
                Native.log('Error while initializing theme: Please provide a theme options object.')
                return
            }
            if (!this.theme) {
                Native.log('Initializing theme...')
                this.theme = new Theme(options)
                this.theme.update(options)
                Playback.update.next({
                    changedData: {
                        currentTimeMs: 0
                    }
                })
            }
            else {
                Native.log('Updating theme...')
                this.theme.update(options)
            }
        })
        Native.on('playback', (options?: Partial<Playback>) => {
            if (!options) {
                Native.log('Error while updating playback: Please provide data within the broadcast.')
                return
            }
            Native.log('Updating playback...' + options.currentTimeMs)
            Playback.update.next({
                changedData: options
            })
        })
    }

    /**
     * Broadcast a message to the connected device.
     * 
     * @param message The topic
     * @param data The associated data
     */
    public static broadcast = (message: SentEvent, data?: string) => {
        if (Native.customMessageHandler) {
            Native.customMessageHandler.sendSynchronous(message, data)
        }
    }

    /**
     * Log a message in the console of the device.
     * 
     * @param message The message
     */
    public static log = (message: string) => {
        Native.broadcast('log', message)
    }

    /**
     * When a message is received from the native environment.
     * 
     * @param message The message
     * @param data The data
     */
    public static on = <T>(message: ReceivedEvent, callback: (data?: T) => void) => {
        if (Native.customMessageHandler) {
            Native.customMessageHandler.on(message, (originalData?: string) => callback(originalData && originalData.startsWith('{') ? JSON.parse(originalData) : originalData))
        }
    }
}
Native.main()